import type {
  IActivityCategory,
  IActivityOccurrence,
  IGroupedHomeVisitActivityOccurrence,
  INonRoutableEmployeePerformedActivityOccurrence,
  INonRoutableEmployeePerformedActivityOccurrenceOrGroup,
} from "@medoma/models/activities";
import {
  activityOccurrenceStatusSchema,
  categorySchema,
  getActivityOccurrenceStatusTimestamp,
  getGroupStatusTimestamp,
  timeOfDaySchema,
  visitStatusSchema,
} from "@medoma/models/activities";
import styles from "./TimelineTile.module.scss";
import clsx from "clsx";
import type { IScheduledShift } from "@medoma/models/shifts";
import {
  calculateVisitTimes,
  getVisitStatusTimestamp,
  isVisitOutsideShiftSpan,
  type IVisit,
} from "@medoma/models/visits";
import { StatusTag } from "@/components/StatusTag/StatusTag";
import { useLocation } from "react-router";
import type { IStatus } from "@/components/StatusTag/statusTagUtils";
import type { IAllocatableItemType } from "../Planning";
import { DragHandle } from "@/components/DragAndDrop/DragHandle";
import { useRef } from "react";
import { useLingui } from "@lingui/react/macro";
import { hasUnfulfilledRequirementsToShowForUnfinishedActivityOccurrence } from "@/pages/commandcenter/Planning/planningViewUtils";
import {
  activityRequirementSchema,
  aggregateRequiredCompetences,
  getUnfulfilledRequirements,
} from "@medoma/models/activityRequirements";
import { CategoryIcon } from "@/components/CategoryIcon/CategoryIcon";
import { Text, TextWithEllipsis } from "@medoma/components/Text/Text";
import { TimeSpan } from "@/components/Time/TimeSpan";
import { WarningsPopover } from "@/components/Warnings/WarningsPopover";
import { ActivityTitle } from "@/components/ActivityTitle/ActivityTitle";
import { getPatientNameWithStatus, type IListPatient } from "@/api/Patients";
import { patientStatusSchema } from "@medoma/models/patients";
import PausedIndicator from "@/components/PausedIndicator/PausedIndicator";
import { FlagsPopover } from "@/components/FlagsPopover/FlagsPopover";
import { KeyToPatientPopover } from "@/components/KeyToPatientPopover/KeyToPatientPopover";
import { PetPopover } from "@/components/PetPopover/PetPopover";
import { calculateGroupStatus } from "../../Activities/ActivitiesTable/getDataFromActivityOccurrences";

type IVariant = "default" | "compact";

type ITileBase = {
  category: IActivityCategory;
  status: IStatus;
  statusTimestamp?: Date;
  patient?: IListPatient | null;
  title?: string;
  timespan:
    | { start: Date; end: Date }
    | typeof timeOfDaySchema.enum.Any
    | undefined;
  warningSlot?: React.ReactNode;
};

type ICompactTile = ITileBase;

type IExpandedTile = ITileBase & {
  activities?: IActivityOccurrence[] | IGroupedHomeVisitActivityOccurrence[];
};

const CompactTile = ({
  category,
  status,
  statusTimestamp,
  patient,
  title,
  timespan,
  warningSlot,
}: ICompactTile) => {
  const statusesToShow = [
    visitStatusSchema.enum.travellingTo,
    visitStatusSchema.enum.ongoing,
    visitStatusSchema.enum.finished,
    activityOccurrenceStatusSchema.enum.ongoing,
    activityOccurrenceStatusSchema.enum.finished,
  ];
  const showStatus = statusesToShow.find((item) => item === status);

  const showFlags = patient && "flags" in patient && !!patient.flags;
  const showKeyToPatient =
    patient && "address" in patient && !!patient.address.keyToPatient;
  const showPet = patient && "pet" in patient && !!patient.pet;
  const hasPatientInfoIndicators = showFlags || showKeyToPatient || showPet;

  return (
    <div
      className={clsx(
        styles.innerTileContainer,
        category === categorySchema.enum.HomeVisit && styles.isHomeVisit,
      )}
    >
      {category === categorySchema.enum.HomeVisit ? null : (
        <div className={styles.category}>
          <CategoryIcon category={category} size="tiny" />
        </div>
      )}
      <div className={styles.patientOrTitleAndWarning}>
        {patient ? (
          <div className={styles.patientAndStatusIndicator}>
            <TextWithEllipsis
              element="div"
              size="small"
              title={getPatientNameWithStatus(patient, "full")}
              maxWidth="8ch"
            >
              {getPatientNameWithStatus(patient, "full")}
            </TextWithEllipsis>
            {patient?.status === patientStatusSchema.enum.admitted &&
            patient?.pausedAt ? (
              <PausedIndicator />
            ) : null}
          </div>
        ) : title ? (
          <TextWithEllipsis
            element="div"
            size="small"
            title={title}
            maxWidth="8ch"
          >
            {title}
          </TextWithEllipsis>
        ) : null}
        {hasPatientInfoIndicators ? (
          <div className={styles.patientInfoIndicators}>
            {showFlags && patient.flags ? (
              <FlagsPopover details={patient.flags} />
            ) : null}
            {showKeyToPatient && patient.address.keyToPatient ? (
              <KeyToPatientPopover details={patient.address.keyToPatient} />
            ) : null}
            {showPet && patient.pet ? (
              <PetPopover details={patient.pet} />
            ) : null}
          </div>
        ) : null}
        {warningSlot}
      </div>
      <div className={styles.statusAndTime}>
        {showStatus ? (
          <StatusTag
            status={status}
            variant="icon-with-timestamp"
            timestamp={statusTimestamp}
            size="tiny"
          />
        ) : timespan ? (
          <TimeSpan timespan={timespan} size="smallest" color="faded" />
        ) : (
          <Text element="div" size="smallest" color="no-results">
            -
          </Text>
        )}
      </div>
    </div>
  );
};

const ExpandedTile = ({
  category,
  status,
  statusTimestamp,
  patient,
  title,
  timespan,
  warningSlot,
  activities,
}: IExpandedTile) => {
  const queryParams = useLocation().search;

  const statusesToShow = [
    visitStatusSchema.enum.travellingTo,
    visitStatusSchema.enum.ongoing,
    visitStatusSchema.enum.finished,
    activityOccurrenceStatusSchema.enum.ongoing,
    activityOccurrenceStatusSchema.enum.finished,
  ];
  const showStatus = statusesToShow.find((item) => item === status);

  return (
    <div
      className={clsx(
        styles.innerTileContainer,
        category === categorySchema.enum.HomeVisit && styles.isHomeVisit,
      )}
    >
      {category === categorySchema.enum.HomeVisit ? null : (
        <div className={styles.category}>
          <CategoryIcon category={category} size="tiny" />
        </div>
      )}
      <div className={styles.patientOrTitleAndWarning}>
        {patient ? (
          <Text element="div" size="small">
            {getPatientNameWithStatus(patient, "full")}
          </Text>
        ) : title ? (
          <Text element="div" size="small">
            {title}
          </Text>
        ) : null}
        {patient && "flags" in patient && patient.flags ? (
          <FlagsPopover details={patient.flags} />
        ) : null}
        {warningSlot}
      </div>
      {activities && activities.length > 0 ? (
        <ul className={styles.activities}>
          {activities.map((activity) => (
            <li key={activity.id} className={styles.activity}>
              <ActivityTitle
                activityOccurrence={activity}
                linkTo={`activities/${activity.activityId}/occurrences/${activity.id}${queryParams}`}
                weight="regular"
                size="smallest"
                showDescriptionAndComment
                showRecurringIcon
              />
            </li>
          ))}
        </ul>
      ) : null}
      <div className={styles.statusAndTime}>
        {showStatus ? (
          <StatusTag
            status={status}
            variant="icon-with-timestamp"
            timestamp={statusTimestamp}
            size="tiny"
          />
        ) : timespan ? (
          <TimeSpan timespan={timespan} size="smallest" color="faded" />
        ) : (
          <Text element="div" size="smallest" color="no-results">
            -
          </Text>
        )}
      </div>
    </div>
  );
};

const tileClassName = (
  status: IStatus,
  variant: IVariant,
  itemType: Exclude<IAllocatableItemType, undefined>,
  isSelected?: boolean,
) => {
  return clsx(
    styles.timeLineTile,
    variant === "compact" ? styles.compactTile : styles.expandedTile,
    status === activityOccurrenceStatusSchema.enum.finished && styles.faded,
    styles[itemType],
    isSelected && styles.selectedTile,
  );
};

export const TimelineGroupTile = ({
  group,
  variant,
  isSelected,
}: {
  group: Extract<
    INonRoutableEmployeePerformedActivityOccurrenceOrGroup,
    { occurrences: unknown }
  >;
  variant: IVariant;
  isSelected?: boolean;
}) => {
  const { t } = useLingui();
  const status = calculateGroupStatus(group);
  const statusTimestamp = getGroupStatusTimestamp(group);
  const isFinished = status === "finished";
  const unfulfilledRequirements = getUnfulfilledRequirements(group);
  const hasExpiredActivities = group.occurrences.some(
    (occurrence) =>
      occurrence.status === activityOccurrenceStatusSchema.enum.expired,
  );
  const warnings = [
    hasExpiredActivities ? t`Innehåller ej genomförda aktiviteter` : null,
    unfulfilledRequirements.length > 0 && !isFinished
      ? t`Kompetensbehov uppfylls inte`
      : null,
  ].filter((warning) => warning !== null);
  const tileProps = {
    category: group.category,
    status,
    statusTimestamp,
    patient: group.patient,
    timespan:
      group.timeOfDay === timeOfDaySchema.enum.Any
        ? timeOfDaySchema.enum.Any
        : { start: group.start, end: group.end },
    warningSlot:
      warnings.length > 0 ? <WarningsPopover warnings={warnings} /> : undefined,
  };

  return (
    <article className={tileClassName(status, variant, "group", isSelected)}>
      <DragHandle />
      {variant === "compact" ? (
        <CompactTile {...tileProps} />
      ) : (
        <ExpandedTile {...tileProps} activities={group.occurrences} />
      )}
    </article>
  );
};

export const TimelineVisitTile = ({
  visit,
  shift,
  variant,
  isSelected,
}: {
  visit: IVisit;
  shift: Pick<IScheduledShift, "startDateTime" | "endDateTime">;
  variant: IVariant;
  isSelected?: boolean;
}) => {
  const { t } = useLingui();
  const { status, occurrences, patient } = visit;
  const statusTimestamp = getVisitStatusTimestamp(visit);
  const { start, end, isAnyTimeOfDay } = calculateVisitTimes(visit);
  const visitHasWellDefinedTime = start && end;
  const visitHasExpiredActivities = occurrences.some(
    (activity) => activity.status === "expired",
  );
  const triggerRef = useRef(null);
  const isFinished = status === visitStatusSchema.enum.finished;

  const unfulfilledRequirements = getUnfulfilledRequirements({
    assignees: visit.assignees,
    requiredCompetences: aggregateRequiredCompetences(visit.occurrences),
    doubleStaffing: visit.occurrences.some(
      (occurrence) => occurrence.doubleStaffing,
    ),
  });
  const hasDoubleStaffingUnfulfilled = unfulfilledRequirements.includes(
    activityRequirementSchema.enum.DoubleStaffing,
  );
  const otherUnfulfilledRequirements = unfulfilledRequirements.filter(
    (requirement) =>
      requirement !== activityRequirementSchema.enum.DoubleStaffing,
  );

  const showDoubleStaffingWarning = hasDoubleStaffingUnfulfilled && !isFinished;
  const showUnfulfilledRequirementsWarning =
    otherUnfulfilledRequirements.length > 0 && !isFinished;
  const showExpiredActivitiesWarning = visitHasExpiredActivities;
  const showOutsideShiftWarning =
    !isFinished && isVisitOutsideShiftSpan(visit, shift);

  const warnings = [
    showExpiredActivitiesWarning
      ? t`Innehåller ej genomförda aktiviteter`
      : null,
    showUnfulfilledRequirementsWarning ? t`Kompetensbehov uppfylls inte` : null,
    showDoubleStaffingWarning ? t`2 medarbetare krävs` : null,
    showOutsideShiftWarning ? t`Besöket ligger utanför passets tid` : null,
  ].filter((warning) => warning !== null);

  const tileProps = {
    category: categorySchema.enum.HomeVisit,
    status,
    statusTimestamp,
    patient,
    timespan: isAnyTimeOfDay
      ? timeOfDaySchema.enum.Any
      : visitHasWellDefinedTime
        ? { start, end }
        : undefined,
    warningSlot:
      warnings.length > 0 ? <WarningsPopover warnings={warnings} /> : undefined,
  };

  return (
    <>
      <article
        ref={triggerRef}
        className={tileClassName(status, variant, "group", isSelected)}
      >
        <DragHandle />
        {variant === "compact" ? (
          <CompactTile {...tileProps} />
        ) : (
          <ExpandedTile {...tileProps} activities={occurrences} />
        )}
      </article>
    </>
  );
};

export const TimelineActivityTile = ({
  activityOccurrence,
  variant,
  isSelected,
}: {
  activityOccurrence: INonRoutableEmployeePerformedActivityOccurrence;
  variant: IVariant;
  isSelected?: boolean;
}) => {
  const { t } = useLingui();
  const triggerRef = useRef(null);

  const { category, start, end, status, patient, timeOfDay } =
    activityOccurrence;
  const statusTimestamp =
    getActivityOccurrenceStatusTimestamp(activityOccurrence);

  const hasUnfulfilledRequirementsToShow =
    hasUnfulfilledRequirementsToShowForUnfinishedActivityOccurrence(
      activityOccurrence,
    );

  const isExpired =
    activityOccurrence.status === activityOccurrenceStatusSchema.enum.expired;

  const warnings = [
    isExpired ? t`Aktiviteten är ej utförd` : null,
    hasUnfulfilledRequirementsToShow ? t`Kompetensbehov uppfylls inte` : null,
  ].filter((warning) => warning !== null);

  const tileProps = {
    category,
    status,
    statusTimestamp,
    patient,
    title: activityOccurrence.title,
    timespan:
      timeOfDay === timeOfDaySchema.enum.Any ? timeOfDay : { start, end },
    warningSlot:
      warnings.length > 0 ? <WarningsPopover warnings={warnings} /> : undefined,
  };

  return (
    <>
      <article
        ref={triggerRef}
        className={tileClassName(
          status,
          variant,
          "activityOccurrence",
          isSelected,
        )}
      >
        <DragHandle />
        {variant === "compact" ? (
          <CompactTile {...tileProps} />
        ) : (
          <ExpandedTile {...tileProps} activities={[activityOccurrence]} />
        )}
      </article>
    </>
  );
};
